import React from 'react';
import {
  Chart as ChartJS,
  BarElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
} from 'chart.js';
import { usePpBarChartQuery, PpBarChartParams } from '../Pallipaarvai.service';
import PPDynamicBarChart from './PPDynamicBarChart';

ChartJS.register(BarElement, CategoryScale, LinearScale, Tooltip, Legend);

interface PPBarChartCntsProps {
  filterParams: PpBarChartParams;
}

// ── API response types ───────────────────────────────────────

interface ObservationTrendItem {
  week: number;
  school_observation: number;
  class_room_observation: number;
}

interface SchoolCategoryItem {
  type_id: number;
  type_name: string;
  completed_count: number;
  total_count: number;
  percentage: number;
}

interface SchoolManagementItem {
  manage_id: number;
  management: string;
  mana_cate_id: number;
  completed_count: number;
  total_count: number;
  percentage: number;
}

interface DesignationItem {
  designation_id: number;
  stackholders: string;
  total_class_target: number;
  completed_count: number;
  total_count: number;
  percentage: number;
}

interface BarChartResult {
  observation_trend: ObservationTrendItem[];
  school_category: SchoolCategoryItem[];
  school_management: SchoolManagementItem[];
  designation: DesignationItem[];
}

// ── Random colors for School Management ──────────────────────

const MANAGEMENT_COLORS = [
  '#E57373',
  '#64B5F6',
  '#81C784',
  '#FFD54F',
  '#BA68C8',
  '#4DB6AC',
  '#FF8A65',
  '#A1887F',
  '#90A4AE',
  '#F06292',
  '#7986CB',
  '#AED581',
  '#FFB74D',
  '#4DD0E1',
  '#CE93D8',
  '#9CCC65',
  '#FFF176',
  '#4FC3F7',
  '#EF5350',
  '#26A69A',
];

// ── Helper: get opacity based on percentage (0-100) ──────────

const getDesignationColor = (percentage: number): string => {
  // Clamp percentage between 0-100
  const clamped = Math.max(0, Math.min(100, percentage));
  // Map percentage to opacity: 0% → 0.2, 100% → 1.0
  const opacity = 0.2 + (clamped / 100) * 0.8;
  // Base color #8A7FC4 → rgba(138, 127, 196, opacity)
  return `rgba(138, 127, 196, ${opacity})`;
};

// ── School Category color map ────────────────────────────────

const CATEGORY_COLOR_MAP: Record<string, string> = {
  Primary: '#8CD38E',
  Middle: '#A5BD63',
  High: '#BD9063',
  'Higher Secondary': '#BD6A63',
};

const getCategoryColor = (typeName: string): string => {
  // Match partial name (API may send "Primary School", "Middle School", etc.)
  for (const [key, color] of Object.entries(CATEGORY_COLOR_MAP)) {
    if (typeName.toLowerCase().includes(key.toLowerCase())) {
      return color;
    }
  }
  return '#9ca3af'; // fallback gray
};

const PPBarChartCnts: React.FC<PPBarChartCntsProps> = ({ filterParams }) => {
  const { data: res, isLoading } = usePpBarChartQuery(filterParams);
  const barData = res?.result as BarChartResult | null;

  // 1. Observation Visit Trends
  const observationChartData = {
    labels: (barData?.observation_trend ?? []).map((d) => `Week ${d.week}`),
    datasets: [
      {
        label: 'School Observation',
        data: (barData?.observation_trend ?? []).map(
          (d) => d.school_observation
        ),
        backgroundColor: '#D75CD9',
        borderRadius: 4,
      },
      {
        label: 'Class Observation',
        data: (barData?.observation_trend ?? []).map(
          (d) => d.class_room_observation
        ),
        backgroundColor: '#5A6C8C',
        borderRadius: 4,
      },
    ],
  };

  // 2. School Category — each category has its own color
  const categoryItems = barData?.school_category ?? [];
  const categoryChartData = {
    labels: categoryItems.map((d) => d.type_name),
    datasets: [
      {
        label: 'Completed',
        data: categoryItems.map((d) => d.completed_count),
        backgroundColor: categoryItems.map((d) =>
          getCategoryColor(d.type_name)
        ),
        borderRadius: 4,
      },
    ],
  };

  // 3. School Management — random colors per bar
  const managementItems = barData?.school_management ?? [];
  const top5 = [...managementItems]
    .sort((a, b) => b.completed_count - a.completed_count)
    .slice(0, 5);

  const managementChartData = {
    labels: top5.map((d) =>
      d.management.length > 15
        ? d.management.slice(0, 15) + '...'
        : d.management
    ),
    datasets: [
      {
        label: 'Completed',
        data: top5.map((d) => d.completed_count),
        backgroundColor: top5.map(
          (_, i) => MANAGEMENT_COLORS[i % MANAGEMENT_COLORS.length]
        ),
        borderRadius: 4,
      },
    ],
  };

  // Full management data for "View All" dialog
  const sortedManagement = [...managementItems].sort(
    (a, b) => b.completed_count - a.completed_count
  );
  const fullManagementChartData = {
    labels: sortedManagement.map((d) => d.management),
    datasets: [
      {
        label: 'Completed',
        data: sortedManagement.map((d) => d.completed_count),
        backgroundColor: sortedManagement.map(
          (_, i) => MANAGEMENT_COLORS[i % MANAGEMENT_COLORS.length]
        ),
        borderRadius: 4,
      },
    ],
  };

  // 4. Designation — color opacity based on completed percentage
  const designationItems = barData?.designation ?? [];
  const designationChartData = {
    labels: designationItems.map((d) => d.stackholders),
    datasets: [
      {
        label: 'Completed',
        data: designationItems.map((d) => d.completed_count),
        backgroundColor: designationItems.map((d) =>
          getDesignationColor(d.percentage)
        ),
        borderRadius: 4,
      },
    ],
  };

  const charts = [
    { title: 'Observation Visit Trends', data: observationChartData },
    { title: 'School Category', data: categoryChartData },
    {
      title: 'School Management - Top 5',
      data: managementChartData,
      fullData: fullManagementChartData,
      tooltipLabels: top5.map((d) => d.management),
    },
    { title: 'Designation', data: designationChartData },
  ];

  if (isLoading) {
    return (
      <div className="row g-3 mt-3">
        {charts.map((c) => (
          <div key={c.title} className="col-12 col-lg-6">
            <div className="card p-3">
              <h6>{c.title}</h6>
              <p className="text-muted">Loading...</p>
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="row g-3 mt-3">
      {charts.map((chart) => (
        <div key={chart.title} className="col-12 col-lg-6">
          <PPDynamicBarChart
            title={chart.title}
            chartData={chart.data}
            fullChartData={chart.fullData}
            tooltipLabels={chart.tooltipLabels}
          />
        </div>
      ))}
    </div>
  );
};

export default PPBarChartCnts;
